import { useState } from 'react';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Search, Download, ChevronDown, ChevronRight,
  Home, Users, Phone, BarChart3, Calendar, Zap, UserPlus,
  Activity, Camera, Shield, Layout, MousePointer, FileText,
  Globe, Smartphone, Mail, MessageSquare, Tag, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  {
    id: 'public',
    title: 'Public Website',
    icon: Globe,
    color: '#D4AF37',
    subsections: [
      {
        id: 'home',
        title: 'Home Page',
        icon: Home,
        steps: [
          { label: 'Hero Section', text: '4 action buttons at the top: Call Now (dials 786-356-0878), Book a Call (opens Calendly 15-min scheduling), Get Pre-Approved (goes to Financing page), Home Value? (goes to Sellers valuation form).' },
          { label: 'Funnel Cards', text: 'Click Buy, Sell, Invest, or Join Our Team to navigate to dedicated funnel pages.' },
          { label: 'Brand Ecosystem', text: 'Shows the 5 brands under the umbrella (SRI, SMF, SRIM, CCS, MMF) with "One Call Handles It All" messaging.' },
          { label: 'Bottom Lead Form', text: 'Fill in Name, Email, Phone, select a service, and submit. The lead goes directly into the CRM pipeline.' },
        ]
      },
      {
        id: 'buyers',
        title: 'Buyers Page',
        icon: Users,
        steps: [
          { label: 'Step 1', text: 'Pre-Approval Banner at top - click to go to Financing page.' },
          { label: 'Step 2', text: 'Property Search Filters - Select Location, Min/Max Price, Bedrooms, Property Type.' },
          { label: 'Step 3 (Lead Gate)', text: 'Before seeing the IDX/MLS property search, you must fill out a short form (Name, Email, Phone). This captures you as a "buyer" lead in the CRM.' },
          { label: 'Step 4', text: 'After submitting the form, the IDX property search iframe unlocks and you can browse MLS listings.' },
          { label: 'Step 5', text: 'Bottom section has buying process steps and a final lead capture form.' },
        ]
      },
      {
        id: 'sellers',
        title: 'Sellers Page',
        icon: Home,
        steps: [
          { label: 'Home Valuation Funnel', text: 'At the top of the page - fill in Name, Email, Phone, and Property Address. Submits as a "seller" lead to the CRM.' },
          { label: 'Selling Process', text: '4-step visual guide: Consultation, Pricing, Marketing, Closing.' },
          { label: 'Benefits', text: 'Why list with Simple Realty.' },
          { label: 'Related Services', text: 'Links to SMF (property management) and SRIM (investments).' },
        ]
      },
      {
        id: 'investors',
        title: 'Investors Page',
        icon: BarChart3,
        steps: [
          { label: 'Off-Market Deals Funnel', text: 'At the top - fill out the lead form to get access to off-market investment deals. Creates an "investor" lead.' },
          { label: 'Investment Types', text: 'Cards for Multi-Family, Fix & Flip, Long-Term Rentals, Commercial.' },
          { label: 'IDX Property Search', text: 'Browse investment properties.' },
          { label: 'Why South Florida', text: 'Section with links to related services (SMF, SRIM, MMF).' },
        ]
      },
      {
        id: 'other-pages',
        title: 'Other Pages',
        icon: Layout,
        steps: [
          { label: 'Financing (/financing)', text: 'Browse loan types: FHA, VA, Conventional, DSCR. Each card explains qualification and use case.' },
          { label: 'Property Management', text: 'Full-service management details for landlords/investors.' },
          { label: 'Open Houses', text: 'Lists upcoming open house events with dates, times, and property details.' },
          { label: 'Team', text: 'All team member profiles with photos, roles, and contact info.' },
          { label: 'Join Our Team', text: 'Agent recruitment application form.' },
          { label: 'Notary', text: 'Certified Closing Services (CCS) information and booking.' },
          { label: 'Resources/Store', text: 'Links to Stan Store and Gumroad for digital products, templates, guides.' },
          { label: 'Contact', text: 'Call (786-356-0878), Text, Email, Calendly booking, and contact form.' },
        ]
      },
      {
        id: 'conversion',
        title: 'Conversion Features',
        icon: MousePointer,
        steps: [
          { label: 'Mobile Sticky Bar', text: 'Fixed bar at bottom on mobile with Call, Text, Book buttons. Always visible when browsing on a phone.' },
          { label: 'Lead Capture Popup', text: 'Automatically appears after 15 seconds on site. Fill in Name, Email, Phone. Can be dismissed with X.' },
          { label: 'Header', text: 'Desktop: Logo, nav links, Call button (gold), Book Now button. Mobile: Hamburger menu with Call/Text/Book.' },
          { label: 'Footer', text: 'Brand ecosystem bar, phone number, social media links (7 platforms), quick nav, Admin Portal link.' },
          { label: 'Click-to-Call', text: '786-356-0878 link present on every single page.' },
          { label: 'Calendly', text: 'All "Book a Call" buttons link to calendly.com/seantherealtor/15min.' },
        ]
      },
    ]
  },
  {
    id: 'crm',
    title: 'CRM / Admin Panel',
    icon: Shield,
    color: '#3B82F6',
    subsections: [
      {
        id: 'access',
        title: 'Accessing the CRM',
        icon: Shield,
        steps: [
          { label: 'Step 1', text: 'Go to /admin (or click "Admin Portal" in the footer).' },
          { label: 'Admin Login', text: 'Email: admin@simplerealty.com | Password: SimpleRealty2024!' },
          { label: 'Agent Login', text: 'Email: agent.mike@test.com | Password: TestPass123!' },
          { label: 'Navigation', text: 'Admin sees 10 nav items. Agent sees 3: My Dashboard, My Leads, My Appointments.' },
        ]
      },
      {
        id: 'dashboard',
        title: 'CRM Dashboard',
        icon: BarChart3,
        steps: [
          { label: 'Period Filter', text: 'Top-right toggle: Today, Week, Month, All. Changes all stats on the page.' },
          { label: '6 Stat Cards', text: 'Total Leads, New Leads, Qualified, Under Contract, Closed, Deal Value. Click any to jump to Pipeline.' },
          { label: 'Pipeline Breakdown', text: 'Visual bar chart showing leads in each of the 10 stages.' },
          { label: 'Lead Tags', text: 'Count of leads by tag (buyer, seller, investor, landlord, financing, notary, agent_recruit).' },
          { label: 'Recent Leads', text: 'Last 8 leads with name, source, tags, stage, date.' },
          { label: 'Upcoming Appointments', text: 'Next 5 scheduled appointments with date, time, type, and status.' },
          { label: 'Agent Dashboard', text: 'When logged in as agent: shows only YOUR assigned leads and appointments. Profile card with photo upload at top.' },
        ]
      },
      {
        id: 'pipeline',
        title: 'Pipeline / CRM (Kanban)',
        icon: Users,
        steps: [
          { label: 'Board View', text: 'Default view - leads as cards in 10 columns: New Lead, Contacted, Qualified, Appointment Set, Consultation, Under Contract, Clear to Close, Closed, Follow-Up, Lost.' },
          { label: 'Drag & Drop', text: 'Drag any lead card from one column to another to change their pipeline stage.' },
          { label: 'Pipeline Filters', text: 'Tabs at top: All, Buyers, Sellers, Investors, Property Mgmt.' },
          { label: 'Table View', text: 'Toggle Board/Table switch. Searchable table. CSV Export button to download filtered leads.' },
          { label: 'Lead Detail Panel', text: 'Click any lead card to open. Edit info, change stage, manage tags, assign agent, add notes.' },
          { label: 'Quick Actions', text: 'Call (dial phone), Log Call (inline logger with outcome/duration/notes), Email (compose).' },
          { label: 'Inline Call Logger', text: 'Select outcome (Answered/Voicemail/No Answer/Busy), set duration (min/sec), add notes, submit.' },
          { label: 'Activity Timeline', text: 'Shows all interactions: calls with outcome badges and duration, stage changes, notes, tag changes, assignments.' },
        ]
      },
      {
        id: 'activities',
        title: 'Activities & Call Tracking',
        icon: Activity,
        steps: [
          { label: 'Stats Cards', text: 'Calls Today, Total Calls, Activities Today, Need Follow-Up (leads with no contact in 7+ days).' },
          { label: 'Call Outcomes', text: 'Breakdown showing count and percentage for Answered, Voicemail, No Answer, Busy.' },
          { label: 'Log a Call', text: 'Click "Log a Call" button. Search and select a lead, pick outcome, enter duration, add notes, submit.' },
          { label: 'Activity Filters', text: 'Filter buttons: All, Call, Email, SMS, Meeting, Note, Task, Stage Change, Tag Change, Assignment.' },
          { label: 'Activity Feed', text: 'Scrollable list of all activities. Each shows type icon, title, lead name, timestamp. Pagination at bottom.' },
        ]
      },
      {
        id: 'appointments',
        title: 'Appointments',
        icon: Calendar,
        steps: [
          { label: 'Create', text: 'Click "New Appointment". Fill Title, select Lead and Agent, pick Date & Time. Choose type: Showing, Consultation, Listing Appointment, Closing, Inspection, Appraisal, Follow-Up, Other.' },
          { label: 'Calendly URL', text: 'Optionally paste a Calendly event URL when creating.' },
          { label: 'Status Filters', text: 'Tabs: All, Scheduled, Confirmed, Completed, Cancelled, No Show.' },
          { label: 'Manage', text: 'Click any appointment to edit details or update status.' },
        ]
      },
      {
        id: 'automations',
        title: 'Automations',
        icon: Zap,
        steps: [
          { label: 'Create Rule', text: 'Click "New Automation". Set Name, choose Trigger (New Lead, Stage Changed, Tag Added), choose Action (Send Email, Send SMS, Add Tag, Move Stage).' },
          { label: 'Enable/Disable', text: 'Toggle switch on each automation card to activate or deactivate.' },
          { label: 'Execution Stats', text: 'View execution count and last executed timestamp on each card.' },
          { label: 'Auto-Execute', text: 'Rules fire automatically when trigger conditions are met (e.g., new lead from website).' },
          { label: 'Note', text: 'Email and SMS sending is currently mocked - logs the action but does not send until Twilio/SendGrid are connected.', highlight: true },
        ]
      },
      {
        id: 'agents',
        title: 'Agent Management',
        icon: UserPlus,
        steps: [
          { label: 'Add Agent', text: 'Click "Add Agent". Fill Full Name, Email, Password, Phone. Click "Create Agent Account". Agent can now log in at /admin.' },
          { label: 'Upload Photo (Admin)', text: 'On each agent card, hover over the photo area (camera icon appears). Click to open file picker. Select JPEG, PNG, WebP, or GIF (max 5MB).' },
          { label: 'Upload Photo (Agent)', text: 'Log in as agent. On My Dashboard, click your profile photo or placeholder. Select an image.' },
          { label: 'Round-Robin', text: 'Click "Round-Robin Assign" to automatically distribute unassigned leads evenly across all agents.' },
          { label: 'Performance Table', text: 'Shows each agent: Assigned, New, Contacted, Qualified, Closed, Lost, Deal Value.' },
          { label: 'Delete Agent', text: 'Click red trash icon. Confirm. Deletes agent and unassigns their leads.' },
        ]
      },
      {
        id: 'roles',
        title: 'Admin vs Agent Access',
        icon: Shield,
        isTable: true,
        tableData: [
          { feature: 'CRM Dashboard', admin: 'Full pipeline stats', agent: 'Own leads only' },
          { feature: 'Pipeline (Kanban)', admin: 'All leads, all stages', agent: 'Own leads only' },
          { feature: 'Activities', admin: 'Global feed', agent: '-' },
          { feature: 'Appointments', admin: 'All appointments', agent: 'Own only' },
          { feature: 'Automations', admin: 'Full CRUD', agent: '-' },
          { feature: 'Agent Management', admin: 'Create/edit/delete', agent: '-' },
          { feature: 'Profile Photo', admin: 'Upload for any agent', agent: 'Own only' },
          { feature: 'Sidebar Badge', admin: 'Gold "ADMIN"', agent: 'Blue "AGENT"' },
        ]
      },
    ]
  },
];

const StepItem = ({ step }) => (
  <div className={`flex gap-3 py-2.5 ${step.highlight ? 'bg-[#D4AF37]/5 rounded-lg px-3 -mx-3' : ''}`}>
    <div className="flex-shrink-0 mt-0.5">
      <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-1.5" />
    </div>
    <div>
      <span className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider">{step.label}</span>
      <p className="text-[#C0C0C0] text-sm mt-0.5 leading-relaxed">{step.text}</p>
    </div>
  </div>
);

const RoleTable = ({ data }) => (
  <div className="overflow-x-auto rounded-lg border border-[#222]">
    <table className="w-full">
      <thead>
        <tr className="bg-[#1A1A1A]">
          <th className="text-left py-2.5 px-4 text-[#808080] text-xs uppercase font-medium">Feature</th>
          <th className="text-center py-2.5 px-4 text-[#D4AF37] text-xs uppercase font-medium">Admin</th>
          <th className="text-center py-2.5 px-4 text-blue-400 text-xs uppercase font-medium">Agent</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={i} className="border-t border-[#1A1A1A]">
            <td className="py-2 px-4 text-[#F5F5F0] text-sm">{row.feature}</td>
            <td className="py-2 px-4 text-center text-[#C0C0C0] text-sm">{row.admin}</td>
            <td className="py-2 px-4 text-center text-[#C0C0C0] text-sm">{row.agent}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const AdminHelp = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({ public: true, crm: true });
  const [expandedSubs, setExpandedSubs] = useState({});

  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleSub = (id) => setExpandedSubs(prev => ({ ...prev, [id]: !prev[id] }));

  const matchesSearch = (text) => {
    if (!searchTerm) return true;
    return text.toLowerCase().includes(searchTerm.toLowerCase());
  };

  const filteredSections = SECTIONS.map(section => ({
    ...section,
    subsections: section.subsections.filter(sub =>
      matchesSearch(sub.title) ||
      sub.steps?.some(s => matchesSearch(s.label) || matchesSearch(s.text)) ||
      sub.tableData?.some(r => matchesSearch(r.feature))
    )
  })).filter(s => s.subsections.length > 0);

  const handleDownload = () => {
    window.open(`${API}/guide/download`, '_blank');
  };

  return (
    <AdminLayout>
      <div data-testid="admin-help-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
              Platform <span className="text-gold-gradient">Guide</span>
            </h1>
            <p className="text-[#808080] mt-1">How to use every feature of your website & CRM</p>
          </div>
          <Button onClick={handleDownload} className="btn-gold" data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> Download PDF
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#808080]" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search features, pages, or instructions..."
            className="pl-12 py-3 bg-[#111111] border-[#333] text-[#F5F5F0] text-base rounded-xl"
            data-testid="help-search"
          />
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {filteredSections.map(section => (
            <div key={section.id} className="bg-[#111111] border border-[#222] rounded-xl overflow-hidden" data-testid={`help-section-${section.id}`}>
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-5 hover:bg-[#1A1A1A]/50 transition-colors"
                data-testid={`toggle-section-${section.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${section.color}15` }}>
                    <section.icon className="w-5 h-5" style={{ color: section.color }} />
                  </div>
                  <h2 className="text-xl text-[#F5F5F0] font-medium" style={{ fontFamily: 'Playfair Display' }}>{section.title}</h2>
                  <span className="text-[#808080] text-xs bg-[#1A1A1A] px-2 py-0.5 rounded">{section.subsections.length} topics</span>
                </div>
                {expandedSections[section.id] ? <ChevronDown className="w-5 h-5 text-[#808080]" /> : <ChevronRight className="w-5 h-5 text-[#808080]" />}
              </button>

              {/* Subsections */}
              {expandedSections[section.id] && (
                <div className="border-t border-[#222]">
                  {section.subsections.map(sub => {
                    const isOpen = expandedSubs[sub.id] !== false;
                    return (
                      <div key={sub.id} className="border-b border-[#1A1A1A] last:border-0" data-testid={`help-topic-${sub.id}`}>
                        <button
                          onClick={() => toggleSub(sub.id)}
                          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[#1A1A1A]/30 transition-colors"
                        >
                          <div className="flex items-center gap-2.5">
                            <sub.icon className="w-4 h-4 text-[#D4AF37]" />
                            <span className="text-[#F5F5F0] text-sm font-medium">{sub.title}</span>
                          </div>
                          {isOpen ? <ChevronDown className="w-4 h-4 text-[#808080]" /> : <ChevronRight className="w-4 h-4 text-[#808080]" />}
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-4 pl-12">
                            {sub.isTable ? (
                              <RoleTable data={sub.tableData} />
                            ) : (
                              <div className="space-y-0.5">
                                {sub.steps?.map((step, i) => <StepItem key={i} step={step} />)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Quick Reference */}
        <div className="mt-8 bg-[#111111] border border-[#D4AF37]/20 rounded-xl p-6" data-testid="quick-reference">
          <h3 className="text-lg text-[#F5F5F0] mb-4" style={{ fontFamily: 'Playfair Display' }}>Quick Reference</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4">
              <Phone className="w-5 h-5 text-[#D4AF37] mb-2" />
              <p className="text-[#F5F5F0] text-sm font-medium">Main Phone</p>
              <a href="tel:7863560878" className="text-[#D4AF37] text-sm hover:underline">786-356-0878</a>
            </div>
            <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4">
              <Calendar className="w-5 h-5 text-[#D4AF37] mb-2" />
              <p className="text-[#F5F5F0] text-sm font-medium">Calendly</p>
              <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noreferrer" className="text-[#D4AF37] text-xs hover:underline">calendly.com/seantherealtor/15min</a>
            </div>
            <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4">
              <Shield className="w-5 h-5 text-[#D4AF37] mb-2" />
              <p className="text-[#F5F5F0] text-sm font-medium">Admin Portal</p>
              <p className="text-[#808080] text-xs">yoursite.com/admin</p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};
